import { useState } from 'react';
import { siteConfig } from '@/mocks/data';

export default function ContactCTASection() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');
  const [submitted, setSubmitted] = useState(false);

  const whatsappHref = `https://wa.me/${siteConfig.whatsappNumber}?text=${encodeURIComponent('Merhaba, size uygun Fineco Isı Pompası sistemi hakkında bilgi almak istiyorum.')}`;

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setSubmitted(true);
      setFormStatus('success');
      return;
    }

    setFormStatus('idle');
    setFormError('');

    try {
      const formData = new FormData(form);
      const response = await fetch('https://readdy.ai/api/form/d9jlcimc26n1c7c5q7a0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && serverMsg.toLowerCase().includes('spam');

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setSubmitted(true);
        setFormStatus('success');
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : 'Bir hata oluştu, lütfen tekrar deneyin.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Bağlantı hatası. Lütfen daha sonra tekrar deneyin.');
    }
  };

  return (
    <section className="py-16 md:py-20 bg-secondary-800">
      <div className="max-w-[1400px] mx-auto px-4 md:px-6">
        <div className="text-center mb-12">
          <h2 className="font-heading text-2xl md:text-4xl text-background-50 mb-4">
            Size Uygun Fineco Isı Pompası Sistemini Öğrenin
          </h2>
          <p className="text-background-50/70 text-sm md:text-base max-w-2xl mx-auto leading-relaxed">
            Bulunduğunuz bölgeyi, ısıtılacak alanı ve mevcut ısınma sisteminizi paylaşın. Size uygun çözüm hakkında bilgi verelim.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto">
          <div className="flex flex-col justify-center">
            <div className="flex flex-col gap-4">
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-[#25D366] text-white px-6 py-4 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-xl w-5 h-5 flex items-center justify-center"></i>
                WhatsApp'tan Bilgi Al
              </a>
              <a
                href={`tel:${siteConfig.phoneRaw}`}
                className="flex items-center gap-3 bg-background-50/10 text-background-50 px-6 py-4 rounded-md text-sm font-semibold hover:bg-background-50/20 transition-colors whitespace-nowrap"
              >
                <i className="ri-phone-line text-xl w-5 h-5 flex items-center justify-center"></i>
                Hemen Ara - {siteConfig.phone}
              </a>
              <a
                href={whatsappHref}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-3 bg-accent-500 text-background-50 px-6 py-4 rounded-md text-sm font-semibold hover:bg-accent-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-price-tag-3-line text-xl w-5 h-5 flex items-center justify-center"></i>
                Fiyat Bilgisi İste
              </a>
            </div>

            <div className="mt-8 flex flex-col gap-3 text-background-50/70 text-sm">
              <div className="flex items-center gap-2">
                <i className="ri-phone-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                <span>{siteConfig.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-map-pin-2-line text-primary-400 w-4 h-4 flex items-center justify-center"></i>
                <span>{siteConfig.serviceArea}</span>
              </div>
            </div>
          </div>

          <div>
            {submitted ? (
              <div className="bg-background-50 rounded-lg p-8 text-center">
                <div className="w-16 h-16 rounded-full bg-primary-100 flex items-center justify-center mx-auto mb-4">
                  <i className="ri-check-line text-primary-600 text-2xl w-8 h-8 flex items-center justify-center"></i>
                </div>
                <h3 className="font-heading text-xl text-foreground-900 mb-3">Talebiniz Alınmıştır</h3>
                <p className="text-foreground-600 text-sm mb-6">En kısa sürede sizinle iletişime geçilecektir.</p>
                <a
                  href={whatsappHref}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#25D366] text-white px-5 py-2.5 rounded-md text-sm font-semibold hover:bg-[#1faa51] transition-colors whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line w-4 h-4 flex items-center justify-center"></i>
                  Beklemeden WhatsApp'tan Yazın
                </a>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="bg-background-50 rounded-lg p-6 md:p-8 flex flex-col gap-4"
              >
                <div>
                  <input
                    type="text"
                    name="ad_soyad"
                    placeholder="Ad Soyad"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="tel"
                    name="telefon"
                    placeholder="Telefon"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="il_ilce"
                    placeholder="İl / İlçe"
                    required
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <input
                    type="text"
                    name="isitilacak_alan"
                    placeholder="Isıtılacak Alan m²"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  />
                </div>
                <div>
                  <select
                    name="hizmet"
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors"
                  >
                    <option value="">İlgilendiğiniz Hizmet</option>
                    <option value="isi_pompasi">Fineco Isı Pompası</option>
                    <option value="kat_kaloriferi">Kat Kaloriferi</option>
                    <option value="gunes_enerjisi">Güneş Enerjisi</option>
                  </select>
                </div>
                <div>
                  <textarea
                    name="mesaj"
                    placeholder="Mesajınız"
                    rows={3}
                    maxLength={500}
                    className="w-full px-4 py-2.5 rounded-md border border-background-200 bg-background-50 text-foreground-900 text-sm focus:outline-none focus:border-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                <input
                  type="text"
                  name="website_alt"
                  className="form-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  readOnly
                />

                {formStatus === 'error' && (
                  <p className="text-accent-500 text-xs">{formError || 'Bir hata oluştu. Lütfen tekrar deneyin.'}</p>
                )}

                <button
                  type="submit"
                  className="w-full bg-primary-500 text-background-50 py-3 rounded-md text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                >
                  Gönder
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}